import * as React from 'react'
import styled from 'styled-components'

import * as api from '@owl-nest/api-client/latest'
import * as hooks from '@owl-nest/hooks'
import * as plume from '@ulule/owl-kit-components/next'

import { Pop } from './Vectors'

type HeroProps = {
  userLocale: api.Lang
}

export function Hero({ userLocale }: HeroProps): React.ReactElement {
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0)

  hooks.useInterval(() => {
    setCurrentImageIndex((currentImageIndex + 1) % images.length)
  }, 3000)

  const content = React.useMemo(() => getHeroBody(userLocale), [])

  return (
    <HeroWrapper>
      <HeroContent>
        <plume.styles.heading.L asDesktop="XXL">
          <Pop />
          {content.title}
        </plume.styles.heading.L>
        <HeroEdito>
          <CarouselWrapper>
            {images.map((image, index) => (
              <CarouselImage
                key={image.id}
                src={image.src}
                alt={`Image ${index + 1}`}
                srcSet={image.srcset}
                active={index === currentImageIndex}
              />
            ))}
          </CarouselWrapper>
          <HeroCard>
            <plume.styles.heading.XXS asDesktop="M">{content.body}</plume.styles.heading.XXS>
          </HeroCard>
        </HeroEdito>
      </HeroContent>
    </HeroWrapper>
  )
}

const images = [
  {
    id: '1',
    src: `${process.env.ASSET_PREFIX}/assets/img/discover/team.png`,
    srcset: `${process.env.ASSET_PREFIX}/assets/img/discover/team@2x.png 2x, ${process.env.ASSET_PREFIX}/assets/img/discover/team@3x.png 3x`,
  },
  {
    id: '2',
    src: `${process.env.ASSET_PREFIX}/assets/img/discover/team_2.png`,
    srcset: `${process.env.ASSET_PREFIX}/assets/img/discover/team_2@2x.png 2x, ${process.env.ASSET_PREFIX}/assets/img/discover/team_2@3x.png 3x`,
  },
  {
    id: '3',
    src: `${process.env.ASSET_PREFIX}/assets/img/discover/team_3.png`,
    srcset: `${process.env.ASSET_PREFIX}/assets/img/discover/team_3@2x.png 2x, ${process.env.ASSET_PREFIX}/assets/img/discover/team_3@3x.png 3x`,
  },
]

interface CarouselImageProps {
  active: boolean
}

const HeroWrapper = styled.div`
  position: relative;
  background-image: url('${process.env.ASSET_PREFIX}/assets/img/discover/bg_shapes_xs.svg');
  background-position: top;
  background-size: contain;
  background-repeat: no-repeat;
  display: flex;
  justify-content: center;
  padding: 40px 20px 0px;
  margin-bottom: 60px;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    max-height: 700px;
    padding: 100px 40px 0px;
    background-image: url('${process.env.ASSET_PREFIX}/assets/img/discover/bg_shapes.svg');
    background-position: 50% 100%;
    background-size: cover;
    margin-bottom: 170px;
  }
`

const HeroContent = styled.div`
  position: relative;
  max-width: 1152px;
  height: 100%;
  min-height: 370px;

  display: grid;
  place-content: space-between;

  ${plume.styles.heading.L} {
    position: relative;
    display: block;
    padding: 0 20px;

    @media screen and ${plume.BREAKPOINTS.TABLET} {
      display: inline;
      text-align: center;
    }
  }

  ${plume.Link} {
    font-weight: inherit;
  }

  ${Pop} {
    position: absolute;
    left: -3%;
    top: -15%;
    transform: scale(0.7);

    @media screen and ${plume.BREAKPOINTS.TABLET} {
      transform: scale(1);
    }

    @media screen and ${plume.BREAKPOINTS.LAPTOP} {
      left: 3%;
      top: -15%;
    }
  }
`

const HeroEdito = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  justify-items: center;
  align-items: flex-end;
  margin-top: 50px;

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    margin-top: 100px;
    grid-template-columns: repeat(12, 1fr);
    grid-template-rows: 370px;
  }
`

const HeroCard = styled.div`
  position: relative;
  max-width: 798px;
  display: flex;
  flex-direction: column;
  padding: 20px;
  background: ${plume.COLORS.PRIMARY_WHITE};
  box-shadow: ${plume.EFFECTS.SHADOW_5};
  border-radius: 4px;
  top: -20px;
  z-index: 2;

  ${plume.styles.heading.M} {
    display: none;
  }

  ${plume.styles.copy.L} {
    display: block;
    font-weight: 600; // HACK: Irregular font manipulation
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    grid-row: 1/2;
    right: 20%;
    top: 20%;
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    grid-column: 4 / -1;
    grid-row: 1 / 2;
    top: 80px;
    right: 0;
    padding: 40px;

    ${plume.styles.heading.M} {
      display: block;
    }

    ${plume.styles.copy.L} {
      display: none;
    }
  }
`

const CarouselWrapper = styled.div`
  position: relative;
  align-self: flex-start;
  width: 200px;
  height: 174px;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    grid-row: 1/2;
    left: 20%;
    width: 336px;
    height: 300px;
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    grid-column: 2 / 5;
    grid-row: 1 / 2;
    left: 0;
  }
`

const CarouselImage = styled.img<CarouselImageProps>`
  position: absolute;
  top: 0;
  left: 0;
  opacity: ${(props) => (props.active ? 1 : 0)};
  transition: opacity 1s ease-in-out;
  width: 100%;
`

function getHeroBody(userLocale: string) {
  if (userLocale === 'fr') {
    return {
      body: (
        <>
          Ulule accompagne celles et ceux qui ont les idées larges et qui veulent faire bouger les choses. <br />
          <br /> Depuis 2010, nous facilitons le passage à l’action et le développement de projets créatifs et durables.
          <br />
          <br /> 50 000 projets ont déjà été financés et des dizaines de milliers de personnes accompagnées, à vous ?
        </>
      ),
      title: (
        <>
          Le meilleur service pour{'\xa0'}
          <plume.Link href="https://fr.ulule.com/projects/create/" kind="primary" tinted>
            se lancer
          </plume.Link>
          , <br />
          <plume.Link href="https://formation.ulule.com/" kind="primary" tinted>
            se former
          </plume.Link>{' '}
          et{' '}
          <plume.Link href="https://fr.ulule.com/discover" kind="primary" tinted>
            financer
          </plume.Link>{' '}
          des projets
        </>
      ),
    }
  } else {
    return {
      body: (
        <>
          Ulule supports people who have big ideas and who want to make a difference. <br />
          <br /> Since 2010, we&apos;ve been helping people take action and develop creative and sustainable projects.
          <br />
          <br /> 50,000 projects have already been funded and tens of thousands of people have been supported.
        </>
      ),
      title: (
        <>
          The top service for{'\xa0'}
          <plume.Link href="https://fr.ulule.com/projects/create/" kind="primary" tinted>
            starting
          </plume.Link>
          , <br />
          <plume.Link href="https://formation.ulule.com/" kind="primary" tinted>
            training
          </plume.Link>{' '}
          and{' '}
          <plume.Link href="https://fr.ulule.com/discover" kind="primary" tinted>
            financing
          </plume.Link>{' '}
          your projects
        </>
      ),
    }
  }
}
